/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Else, If, Then, Unless } from "react-if";
import { theme } from "@nordcloud/gnui";
import { DataContainer, DataItem, DateRange, Separator } from "~/components";
import { EstatePeriod } from "~/constants";
import { useCurrency } from "~/hooks";
import { formatMoney, isNotNil } from "~/tools";
import { PeriodSelection } from "../Views";

type Props = {
  period: EstatePeriod;
  range?: DateRange;
  totalCost?: number;
  count?: number | null;
  monthlySavings?: string | null;
  isLoading?: boolean;
  forecast?: string;
  averageCost?: string;
  hidePeriod?: boolean;
};

export function AccumulatedCostData({
  period,
  range,
  totalCost,
  count,
  monthlySavings,
  forecast,
  averageCost,
  isLoading = false,
  hidePeriod = false,
}: Props) {
  const { currency } = useCurrency();

  return (
    <>
      <Unless condition={hidePeriod}>
        <PeriodSelection period={period} range={range} />
        <Separator marginBottom={theme.spacing.spacing04} />
      </Unless>
      <DataContainer width="100%">
        <DataItem
          label="Total Cost"
          value={formatMoney(totalCost, currency)}
          grow={false}
          loading={isLoading}
          valueFontWeight={theme.fontWeights.medium}
        />
        <If condition={period === EstatePeriod.RANGE}>
          <Then>
            <DataItem
              label="Average monthly cost"
              value={formatMoney(averageCost, currency)}
              grow={false}
              loading={isLoading}
              valueFontWeight={theme.fontWeights.medium}
            />
          </Then>
          <Else>
            <DataItem
              label="Monthly Forecast"
              value={isNotNil(forecast) ? formatMoney(forecast, currency) : "-"}
              grow={false}
              loading={isLoading}
              valueFontWeight={theme.fontWeights.medium}
            />
            <DataItem
              label="Potential Monthly Savings"
              grow={false}
              valueFontWeight={theme.fontWeights.medium}
              loading={isLoading}
              value={
                isNotNil(monthlySavings)
                  ? formatMoney(monthlySavings, currency)
                  : "-"
              }
            />
            <DataItem
              label="Next 12 Months Savings"
              grow={false}
              valueFontWeight={theme.fontWeights.medium}
              loading={isLoading}
              value={
                isNotNil(monthlySavings)
                  ? formatMoney(Number(monthlySavings) * 12, currency)
                  : "-"
              }
            />
            <DataItem
              label="Total Estate Records"
              value={isNotNil(count) ? count?.toString() : "-"}
              grow={false}
              valueFontWeight={theme.fontWeights.medium}
              loading={isLoading}
            />
          </Else>
        </If>
      </DataContainer>
    </>
  );
}
